import { TextValueFormat } from 'editor/src/store/design/types';

/**
 * Longest pattern an author can save. Kept short for two reasons: a runaway
 * pattern is easier to spot and fix while it is still on screen, and it bounds
 * how much backtracking a single `test()` call can possibly do — see the ReDoS
 * note on `compileValueRegex`.
 */
export const MAX_VALUE_REGEX_LENGTH = 500;

/** Flags that keep `test()` idempotent across repeated calls with the same value. */
const KEPT_FLAGS = new Set(['i', 's', 'u', 'm']);

/**
 * A pattern string in the `/body/flags` literal form, recognised only when it
 * both opens and closes with "/" — a bare pattern is free to contain "/"
 * anywhere (e.g. a date format "\d{2}/\d{2}/\d{4}"), so only the closing
 * delimiter followed by nothing but letters can be told apart from that.
 */
const LITERAL_FORM = /^\/(.*)\/([a-zA-Z]*)$/;

/**
 * Compiled patterns, keyed by the exact string the author wrote.
 *
 * Every keystroke on the pattern field and every keystroke on the buyer's
 * value re-runs validation, so recompiling the same pattern on each one would
 * be wasted work. Bounded rather than a plain Map that grows forever: an
 * editing session can churn through many draft patterns as the author types,
 * and none of the earlier ones are worth keeping once the author has moved on.
 */
const cache = new Map<string, RegExp | null>();
const MAX_CACHE_SIZE = 200;

/**
 * Compiles an author's pattern into the RegExp the buyer's value is tested
 * against, or `null` when the pattern cannot be enforced.
 *
 * Accepts either a bare pattern ("[A-Z]{2}-\d{4}") or the `/body/flags`
 * literal form ("/[a-z]+/i"), so an author who is used to writing regex
 * literals does not have to strip the slashes themselves.
 *
 * The body is always wrapped as `^(?:body)$`. The non-capturing group is
 * required: without it, `a|b` would anchor only its right-hand side and match
 * any string ending in "b", not just "a" or "b" in full.
 *
 * `g` and `y` are dropped even when the author wrote them explicitly. Both
 * make `test()` stateful through `lastIndex`, which would make the exact same
 * value alternate between matching and not matching on repeated checks — and
 * validation calls `test()` on every store change, not once.
 *
 * Returns `null` for an empty or over-length pattern, or one that fails to
 * compile — never throws. An author pattern is free text, typed one keystroke
 * at a time, so it is invalid far more often than a hand-written literal ever
 * is, and every caller treats `null` as "not enforceable" rather than an error.
 */
export function compileValueRegex(pattern: string): RegExp | null {
  if (cache.has(pattern)) {
    return cache.get(pattern)!;
  }

  const compiled = compile(pattern);

  if (cache.size >= MAX_CACHE_SIZE) {
    cache.clear();
  }
  cache.set(pattern, compiled);

  return compiled;
}

function compile(pattern: string): RegExp | null {
  if (!pattern || pattern.length > MAX_VALUE_REGEX_LENGTH) {
    return null;
  }

  const literalMatch = pattern.match(LITERAL_FORM);
  const body = literalMatch ? literalMatch[1] : pattern;
  const flagChars = literalMatch ? literalMatch[2] : '';

  if (!body) {
    return null;
  }

  const flags = [...new Set(flagChars)].filter((flag) => KEPT_FLAGS.has(flag)).join('');

  try {
    return new RegExp(`^(?:${body})$`, flags);
  } catch {
    return null;
  }
}

/** Whether an author's pattern is one `compileValueRegex` can enforce. */
export function isValidValueRegex(pattern: string): boolean {
  return compileValueRegex(pattern) !== null;
}

/**
 * Whether a buyer's value satisfies a text's forced format.
 *
 * True for anything that is not a regex format, for a pattern that fails to
 * compile, and for an empty value — an invalid pattern is never enforced (an
 * author error must not strand the buyer on a field nobody can satisfy), and
 * emptiness is Required's job, so an optional field can stay blank.
 */
export function matchesValueFormat(value: string, format: TextValueFormat | undefined): boolean {
  if (!format || format.kind !== 'regex' || value === '') {
    return true;
  }

  const regex = compileValueRegex(format.pattern);
  return !regex || regex.test(value);
}
